import { Link } from 'react-router';
import React, { useCallback, useEffect, useRef } from 'react';
import { useAtomValue } from 'jotai';
import { uiContextAtomTileActions } from '@rocco/ui/tile-shared/hooks/use-shared-tiles';
import type { TileViewModel } from '@rocco/ui/tile-shared/view-models/tile';
import type { ProductTileViewModel } from '@rocco/ui/tile-product/view-models/product';
import { Image } from '@rocco/components/image';
import { cn } from '@rocco/utils/cn';
import { useLogImpression, useTracker } from '@archipro-website/tracker';
import { trackTileMediaDetailClick } from '~/modules/tracking/util/trackTileMedia';
import { useTileAdminActions } from '@modules-rocco/admin/hooks/use-tile-admin-actions';
import {
    TileOverlayWithFollowSelector,
    TileMobileActionsSlot,
} from '~/modules/moodboards/components';
import {
    TileEnquiryOverlay,
    useBulkEnquirySelectActive,
    useTileEnquiryClickCapture,
} from '~/modules/enquiry/bulk/BulkEnquireTileControl';
import { AdminTile } from '@rocco/ui/tile-shared/components/AdminTile';
import { TextTooltip } from '~/ui-rocco/components/TextTooltip';
import { useRootPermissions } from '~/modules/user';
import { useLibraryAction } from '~/modules/directory/context/LibraryActionContext';
import { useProductTileHeroStyle } from '@modules/product/util/product-hero-view-transition';
import { setPendingHeroImage } from '@rocco/ui/tile-shared/hooks/use-tile-hero-view-transition';
import { uiContextAtomViewTransitionsEnabled } from '@rocco/states/uiContextAtomViewTransitions';
import { useTileViewTransitionScope } from '@rocco/contexts/TileViewTransitionScopeContext';

interface DirectoryProductTileProps {
    tile: TileViewModel<ProductTileViewModel>;
    isPending?: boolean;
    isFollowed?: boolean;
}

// Selector: editProfile is mount-stable, so the picked branch never swaps
// during a session and the chosen tile's <img> is never remounted.
export const DirectoryProductTile = React.memo(
    (props: DirectoryProductTileProps) => {
        const { editProfile } = useRootPermissions();
        return editProfile ? (
            <AdminDirectoryProductTile {...props} />
        ) : (
            <RegularDirectoryProductTile {...props} />
        );
    },
);

DirectoryProductTile.displayName = 'DirectoryProductTile';

const RegularDirectoryProductTile = ({
    tile,
    isPending = false,
}: DirectoryProductTileProps) => {
    const tileRef = useRef<HTMLDivElement>(null);
    const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const tracker = useTracker();
    const { model } = tile;
    const primaryImage = model.images?.[0];
    const tileActions = useAtomValue(uiContextAtomTileActions);
    // Both hooks run unconditionally to keep the hook order stable.
    const viewTransitionsFlagOn = useAtomValue(
        uiContextAtomViewTransitionsEnabled,
    );
    const inTileTransitionScope = useTileViewTransitionScope();
    const viewTransitionsEnabled =
        viewTransitionsFlagOn && inTileTransitionScope;
    const heroStyle = useProductTileHeroStyle(
        viewTransitionsEnabled ? model.link : undefined,
    );

    const handleMouseEnter = useCallback(() => {
        hoverTimerRef.current = setTimeout(() => {
            tileActions?.trackingConfig?.onHover?.(
                tileRef as React.RefObject<HTMLElement>,
                model.id,
                model.professional?.id,
            );
        }, 500);
    }, [tileActions, model.id, model.professional?.id]);

    const handleMouseLeave = useCallback(() => {
        if (hoverTimerRef.current) {
            clearTimeout(hoverTimerRef.current);
            hoverTimerRef.current = null;
        }
    }, []);

    useEffect(() => {
        return () => {
            if (hoverTimerRef.current) {
                clearTimeout(hoverTimerRef.current);
            }
        };
    }, []);

    const handleClick = () => {
        if (viewTransitionsEnabled) {
            setPendingHeroImage(
                model.link,
                tileRef.current?.querySelector('img')?.currentSrc,
            );
        }
        trackTileMediaDetailClick(tracker, {
            itemID: model.id,
            itemType: 'ProductTile',
            itemName: model.title,
        });
    };

    // Bulk-enquiry mode: the whole tile toggles the selection instead of
    // navigating (capture phase, so it beats the link).
    const enquiryClickCapture = useTileEnquiryClickCapture(model);
    const bulkSelectActive = useBulkEnquirySelectActive(model);

    useLogImpression({
        data: {
            type: 'LibraryItem_Directory',
            itemID: model.id,
            professionalID: model.professional?.id ?? 0,
        },
        ref: tileRef,
    });

    return (
        <div
            ref={tileRef}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onClickCapture={enquiryClickCapture}
            className={cn(
                'group relative transition-opacity duration-300',
                isPending && 'opacity-60',
            )}
        >
            <Link
                to={model.link}
                prefetch="intent"
                viewTransition={viewTransitionsEnabled}
                onClick={handleClick}
                className="flex flex-col gap-[0.9375rem]"
                aria-label={`Product: ${model.title}`}
                data-product-tile
            >
                {/* The rounding and clip live on this box, so naming it (rather
                    than the <img> inside) keeps the corners in the snapshot —
                    an ancestor's overflow is not applied to a captured element. */}
                <div
                    className="aspect-square w-full overflow-hidden rounded-lg"
                    style={heroStyle}
                >
                    <div className="relative h-full overflow-hidden bg-[#FAFAFA]">
                        {primaryImage && (
                            <Image
                                src={primaryImage.src}
                                alt=""
                                fill
                                fit={
                                    primaryImage.multiplied
                                        ? 'contain'
                                        : 'cover'
                                }
                                multiplied={primaryImage.multiplied}
                                sizes="(min-width: 1921px) 348px, ((min-width: 1024px) and (max-width: 1920px)) 18vw, 49vw"
                                className="transition-transform duration-300 group-hover:scale-105"
                            />
                        )}

                        <TileEnquiryOverlay
                            model={model}
                            follow={
                                !tile.hideFollowButton && (
                                    <TileOverlayWithFollowSelector
                                        pinId={model.id}
                                        pinType="ProductPin"
                                        enableActionPanel
                                        enquiry={
                                            model.professional?.link
                                                ? {
                                                      itemType: 'product',
                                                      siteTreeID: model.id,
                                                      professionalId:
                                                          model.professional
                                                              ?.id ?? 0,
                                                      professionalName:
                                                          model.professional
                                                              ?.name ?? '',
                                                      professionalLink:
                                                          model.professional
                                                              .link,
                                                      itemTitle: model.title,
                                                      itemImage:
                                                          primaryImage?.src,
                                                  }
                                                : null
                                        }
                                        share={{
                                            url: model.link,
                                            title: model.title,
                                        }}
                                        tileInfo={{
                                            title: model.title,
                                            subtitle: model.professional?.name,
                                            image: primaryImage?.src,
                                        }}
                                    />
                                )
                            }
                        />
                    </div>
                </div>

                <div className="flex flex-col gap-0.5">
                    <TextTooltip content={model.title}>
                        <p className="line-clamp-1 text-lg leading-[1.3] font-medium tracking-[-0.0225rem] text-[#141414]">
                            {model.title}
                        </p>
                    </TextTooltip>
                    <TextTooltip content={model.professional?.name ?? ''}>
                        <p className="line-clamp-1 text-base leading-[1.3] font-medium tracking-[-0.02rem] text-[#737373]">
                            {model.professional?.name}
                        </p>
                    </TextTooltip>
                </div>
            </Link>
            {/* Mobile actions live outside the tile link so a tap on the `•••`
                or a sheet-backdrop dismiss can never navigate the tile. */}
            {!tile.hideFollowButton && !bulkSelectActive ? (
                <TileMobileActionsSlot
                    pinId={model.id}
                    pinType="ProductPin"
                    enableActionPanel
                    enquiry={
                        model.professional?.link
                            ? {
                                  itemType: 'product',
                                  siteTreeID: model.id,
                                  professionalId: model.professional?.id ?? 0,
                                  professionalName:
                                      model.professional?.name ?? '',
                                  professionalLink: model.professional.link,
                                  itemTitle: model.title,
                                  itemImage: primaryImage?.src,
                              }
                            : null
                    }
                    share={{ url: model.link, title: model.title }}
                    tileInfo={{
                        title: model.title,
                        subtitle: model.professional?.name,
                        image: primaryImage?.src,
                    }}
                />
            ) : null}
        </div>
    );
};

const AdminDirectoryProductTile = ({
    tile,
    isPending = false,
}: DirectoryProductTileProps) => {
    const tileRef = useRef<HTMLDivElement>(null);
    const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const tracker = useTracker();
    const { model } = tile;
    const primaryImage = model.images?.[0];
    const tileActions = useAtomValue(uiContextAtomTileActions);
    // Both hooks run unconditionally to keep the hook order stable.
    const viewTransitionsFlagOn = useAtomValue(
        uiContextAtomViewTransitionsEnabled,
    );
    const inTileTransitionScope = useTileViewTransitionScope();
    const viewTransitionsEnabled =
        viewTransitionsFlagOn && inTileTransitionScope;
    const heroStyle = useProductTileHeroStyle(
        viewTransitionsEnabled ? model.link : undefined,
    );

    const { removeFromLibrary, removedFromLibraryIds } = useLibraryAction();
    const isFadingOut = removedFromLibraryIds?.has(model.id) ?? false;
    const adminActions = useTileAdminActions({
        item: {
            id: model.id.toString(),
            link: model.link ?? '',
            professionalLink: model.professional?.link ?? '',
            pinned: tile.adminStatusInfo?.pinned,
            featuredBrandPinned: tile.adminStatusInfo?.featuredBrandPinned,
            productsAllowed: tile.adminStatusInfo?.productsAllowed,
        },
        itemType: 'products',
        onRemoveFromLibrary:
            removeFromLibrary && !isFadingOut
                ? () =>
                      removeFromLibrary('product', model.id, model.title ?? '')
                : undefined,
    });

    const handleMouseEnter = useCallback(() => {
        hoverTimerRef.current = setTimeout(() => {
            tileActions?.trackingConfig?.onHover?.(
                tileRef as React.RefObject<HTMLElement>,
                model.id,
                model.professional?.id,
            );
        }, 500);
    }, [tileActions, model.id, model.professional?.id]);

    const handleMouseLeave = useCallback(() => {
        if (hoverTimerRef.current) {
            clearTimeout(hoverTimerRef.current);
            hoverTimerRef.current = null;
        }
    }, []);

    useEffect(() => {
        return () => {
            if (hoverTimerRef.current) {
                clearTimeout(hoverTimerRef.current);
            }
        };
    }, []);

    const handleClick = () => {
        if (viewTransitionsEnabled) {
            setPendingHeroImage(
                model.link,
                tileRef.current?.querySelector('img')?.currentSrc,
            );
        }
        trackTileMediaDetailClick(tracker, {
            itemID: model.id,
            itemType: 'ProductTile',
            itemName: model.title,
        });
    };

    // Bulk-enquiry mode: the whole tile toggles the selection instead of
    // navigating (capture phase, so it beats the link).
    const enquiryClickCapture = useTileEnquiryClickCapture(model);
    const bulkSelectActive = useBulkEnquirySelectActive(model);

    useLogImpression({
        data: {
            type: 'LibraryItem_Directory',
            itemID: model.id,
            professionalID: model.professional?.id ?? 0,
        },
        ref: tileRef,
    });

    return (
        <AdminTile tile={tile} actions={adminActions}>
            <div
                ref={tileRef}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onClickCapture={enquiryClickCapture}
                className={cn(
                    'group relative transition-all duration-500 ease-in-out',
                    isPending && 'opacity-60',
                    isFadingOut && 'pointer-events-none scale-95 opacity-0',
                )}
            >
                <Link
                    to={model.link}
                    prefetch="intent"
                    viewTransition={viewTransitionsEnabled}
                    onClick={handleClick}
                    className="flex flex-col gap-3.75"
                    aria-label={`Product: ${model.title}`}
                    data-product-tile
                >
                    <div
                        className="aspect-square w-full overflow-hidden rounded-lg"
                        style={heroStyle}
                    >
                        <div className="relative h-full overflow-hidden bg-[#FAFAFA]">
                            {primaryImage && (
                                <Image
                                    src={primaryImage.src}
                                    alt=""
                                    fill
                                    fit={
                                        primaryImage.multiplied
                                            ? 'contain'
                                            : 'cover'
                                    }
                                    multiplied={primaryImage.multiplied}
                                    sizes="(min-width: 1921px) 348px, ((min-width: 1024px) and (max-width: 1920px)) 18vw, 49vw"
                                    className="transition-transform duration-300 group-hover:scale-105"
                                />
                            )}

                            <TileEnquiryOverlay
                                model={model}
                                follow={
                                    !tile.hideFollowButton && (
                                        <TileOverlayWithFollowSelector
                                            pinId={model.id}
                                            pinType="ProductPin"
                                            enableActionPanel
                                            enquiry={
                                                model.professional?.link
                                                    ? {
                                                          itemType: 'product',
                                                          siteTreeID: model.id,
                                                          professionalId:
                                                              model.professional
                                                                  ?.id ?? 0,
                                                          professionalName:
                                                              model.professional
                                                                  ?.name ?? '',
                                                          professionalLink:
                                                              model.professional
                                                                  .link,
                                                          itemTitle:
                                                              model.title,
                                                          itemImage:
                                                              primaryImage?.src,
                                                      }
                                                    : null
                                            }
                                            share={{
                                                url: model.link,
                                                title: model.title,
                                            }}
                                            tileInfo={{
                                                title: model.title,
                                                subtitle:
                                                    model.professional?.name,
                                                image: primaryImage?.src,
                                            }}
                                        />
                                    )
                                }
                            />
                        </div>
                    </div>

                    <div className="flex flex-col gap-0.5">
                        <TextTooltip content={model.title}>
                            <p className="line-clamp-1 text-lg leading-[1.3] font-medium tracking-[-0.0225rem] text-[#141414]">
                                {model.title}
                            </p>
                        </TextTooltip>
                        <TextTooltip content={model.professional?.name ?? ''}>
                            <p className="line-clamp-1 text-base leading-[1.3] font-medium tracking-[-0.02rem] text-[#737373]">
                                {model.professional?.name}
                            </p>
                        </TextTooltip>
                    </div>
                </Link>
                {/* Mobile actions live outside the tile link so a tap on the
                    `•••` or a sheet-backdrop dismiss can never navigate. */}
                {!tile.hideFollowButton && !bulkSelectActive ? (
                    <TileMobileActionsSlot
                        pinId={model.id}
                        pinType="ProductPin"
                        enableActionPanel
                        enquiry={
                            model.professional?.link
                                ? {
                                      itemType: 'product',
                                      siteTreeID: model.id,
                                      professionalId:
                                          model.professional?.id ?? 0,
                                      professionalName:
                                          model.professional?.name ?? '',
                                      professionalLink: model.professional.link,
                                      itemTitle: model.title,
                                      itemImage: primaryImage?.src,
                                  }
                                : null
                        }
                        share={{ url: model.link, title: model.title }}
                        tileInfo={{
                            title: model.title,
                            subtitle: model.professional?.name,
                            image: primaryImage?.src,
                        }}
                    />
                ) : null}
            </div>
        </AdminTile>
    );
};
